import { useEffect, useMemo, useRef, useState } from "react";
import { STAGES } from "@/lib/mh";
import { mid, reduceMotion, isTouch, deptTotals, roleStatus } from "@/lib/sky";
import { Panel } from "@/components/mh/bits";
import { StatusChip, Tooltip, HubTip } from "@/components/mh/SkyParts";
import { buildSky, CX, CY } from "@/components/mh/SkySvg";

const OFFS = [-0.3, -0.1, 0.1, 0.3];

function layout(deps, agents) {
  const nodes = [], hubs = [], chains = [];
  deps.forEach((d, i) => {
    const ang = (i / deps.length) * Math.PI * 2 - Math.PI / 2;
    const at = (r, a = ang) => ({ x: CX + Math.cos(a) * r, y: CY + Math.sin(a) * r });
    const hub = { ...d, ...at(150), lx: at(490).x, ly: at(490).y + 6, fx: at(300).x, fy: at(300).y };
    hubs.push(hub);
    let j = 0;
    STAGES.forEach((s, si) => {
      const list = agents.filter((a) => a.department === d.slug && a.stage === s);
      const ns = list.map((a, k) => ({ ...a, hex: d.hex, j: j++, ...at(205 + k * 33, ang + OFFS[si] * (1 - k * 0.04) + (k % 2 ? 0.035 : -0.035)) }));
      nodes.push(...ns);
      chains.push({ hub, nodes: ns });
    });
  });
  return { nodes, hubs, chains };
}

const HOME = { x: 0, y: 0, k: 1 };
const zoomed = (hb, k = 2.6) => ({ x: -k * (hb.fx - CX), y: -k * (hb.fy - CY), k });
export const SKY_FROM = "mh-sky-from";

export function SkyView({ s, isT, filter, mode, onOpenRole, onEnter, fly }) {
  const { deps, agents, onbSt, settled, iv, onbActive, needs } = s;
  const [hover, setHover] = useState(null);
  const [hubHover, setHubHover] = useState(null);
  const [tapped, setTapped] = useState(null);
  const [view, setView] = useState(HOME);
  const [anim, setAnim] = useState(false);
  const [focus, setFocus] = useState(null);
  const drag = useRef(null);
  const { nodes, hubs, chains } = useMemo(() => layout(deps, agents), [deps, agents]);
  const maxMid = useMemo(() => Math.max(1, ...agents.filter((a) => a.tailored?.enabled).map(mid)), [agents]);
  const job = onbSt?.job;

  const enter = (hb, role) => {
    const ms = reduceMotion() ? 0 : 800;
    setHover(null); setHubHover(null); setAnim(true); setFocus(hb.slug); setView(zoomed(hb));
    setTimeout(() => { sessionStorage.setItem(SKY_FROM, hb.slug); onEnter(hb.slug, role); }, ms);
  };
  useEffect(() => {
    const from = sessionStorage.getItem(SKY_FROM), hb = hubs.find((x) => x.slug === from);
    if (!hb) return;
    sessionStorage.removeItem(SKY_FROM);
    if (reduceMotion()) return;
    setAnim(false); setFocus(hb.slug); setView(zoomed(hb));
    const t = setTimeout(() => { setAnim(true); setView(HOME); setFocus(null); }, 40);
    return () => clearTimeout(t);
  }, [hubs.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const hb = fly && hubs.find((x) => x.slug === fly.dept); if (hb) enter(hb, fly.role); }, [fly]); // eslint-disable-line react-hooks/exhaustive-deps

  const radius = (n) => (!isT || !n.tailored ? 4 : !n.tailored.enabled ? 3.5 : 4 + 8 * Math.sqrt(mid(n) / maxMid));
  const match = (n) => {
    const t = n.tailored;
    if (filter === "all") return true;
    if (filter === "off") return t && !t.enabled;
    if (filter === "needs") return needs(n.department);
    return t?.enabled && t.wave === Number(filter[1]);
  };
  const onWheel = (e) => { setAnim(false); setView((v) => ({ ...v, k: Math.min(3, Math.max(0.6, v.k * (e.deltaY < 0 ? 1.1 : 0.9))) })); };
  const onDown = (e) => { setAnim(false); drag.current = { sx: e.clientX, sy: e.clientY, ...view }; };
  const onMove = (e) => { if (drag.current) setView((v) => ({ ...v, x: drag.current.x + (e.clientX - drag.current.sx), y: drag.current.y + (e.clientY - drag.current.sy) })); };
  const clickNode = (n) => { if (isTouch() && tapped !== n.slug) { setTapped(n.slug); setHover(n); return; } onOpenRole(n.slug); };
  const hubTot = hubHover && deptTotals(agents.filter((a) => a.department === hubHover.slug), (a) => roleStatus(a, needs(hubHover.slug)));

  const sky = buildSky({ hubs, nodes, chains, view, isT, iv, onbActive, needs, filter, job, settled, radius, match, hover: hover?.slug,
    anim, focus, hubHover: hubHover?.slug, onHubHover: setHubHover,
    onHover: setHover, onNode: clickNode, onHub: (hb) => enter(hb),
    handlers: { onWheel, onMouseDown: onDown, onMouseMove: onMove, onMouseUp: () => (drag.current = null), onMouseLeave: () => (drag.current = null) } });

  return (
    <Panel cls="relative overflow-hidden h-[68vh] sm:h-[82vh] bg-[#030305] cursor-grab active:cursor-grabbing select-none" testId="constellation-canvas">
      {sky}
      <StatusChip job={job} deps={deps} />
      {hubHover && !focus && <HubTip hb={hubHover} tot={hubTot} />}
      {hover && !focus && <Tooltip n={hover} mode={mode} hint={tapped === hover.slug && isTouch()} />}
      <div className="absolute right-4 bottom-4 font-mono text-[10px] text-muted-foreground hidden sm:block">drag to pan · wheel to zoom · click a department to fly in</div>
    </Panel>
  );
}
